import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import {
  activateEvaluationRound,
  addEvaluationRatersByJobTitle,
  cloneEvaluationRound,
  closeEvaluationRound,
  createEvaluationRound,
  deleteEvaluationRound,
  getEvaluationRound,
  getEvaluationRounds,
  setEvaluationSubjectRaters,
  spreadEvaluationRound,
  updateEvaluationRound,
} from "../../api/evaluations";
import EvaluationComparisonChart from "../common/EvaluationComparisonChart";
import "./HrEvaluations.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function statusLabel(status) {
  if (status === "DRAFT") return "مسودة";
  if (status === "ACTIVE") return "نشطة";
  if (status === "CLOSED") return "مغلقة";
  return status || "—";
}

function raterTypeLabel(type) {
  if (type === "MANAGER") return "المدير المباشر";
  if (type === "PEER") return "الزملاء / الأقران";
  if (type === "SUBORDINATE") return "المرؤوسون";
  if (type === "SELF") return "تقييم ذاتي";
  if (type === "CUSTOM") return "مخصص";
  if (type === "DESIGNATED") return "مقيّم محدد";
  return type || "—";
}

const AUDIENCE_OPTIONS = [
  { key: "MANAGER", label: "مدير" },
  { key: "PEER", label: "زملاء" },
  { key: "SUBORDINATE", label: "مرؤوسون" },
];

/** Shared question bank: self always answers all; audiences = who else answers. */
const DEFAULT_QUESTIONS = [
  {
    text: "تحقيق الأهداف الشهرية",
    weightPercent: "15",
    audiences: ["MANAGER"],
  },
  {
    text: "جودة تنفيذ العمل",
    weightPercent: "15",
    audiences: ["MANAGER"],
  },
  {
    text: "الاستجابة للتوجيه والإشراف",
    weightPercent: "10",
    audiences: ["MANAGER"],
  },
  {
    text: "التعاون مع الزملاء",
    weightPercent: "10",
    audiences: ["PEER"],
  },
  {
    text: "روح الفريق والمساعدة",
    weightPercent: "10",
    audiences: ["PEER"],
  },
  {
    text: "التواصل داخل الفريق",
    weightPercent: "10",
    audiences: ["PEER"],
  },
  {
    text: "مهارات القيادة",
    weightPercent: "10",
    audiences: ["SUBORDINATE"],
  },
  {
    text: "وضوح التوجيه والإدارة",
    weightPercent: "10",
    audiences: ["SUBORDINATE"],
  },
  {
    text: "التواصل مع الفريق",
    weightPercent: "10",
    audiences: ["SUBORDINATE"],
  },
];

function buildDefaultQuestions() {
  return DEFAULT_QUESTIONS.map((q, index) => ({
    text: q.text,
    weightPercent: q.weightPercent,
    maxScore: 5,
    audiences: [...q.audiences],
    targetEmployeeIds: [],
    raterEmployeeIds: [],
    sortOrder: index,
  }));
}

function emptyQuestion() {
  return {
    text: "",
    weightPercent: "",
    maxScore: 5,
    audiences: ["MANAGER"],
    targetEmployeeIds: [],
    raterEmployeeIds: [],
  };
}

function audienceLabels(audiences) {
  const list = Array.isArray(audiences) ? audiences : [];
  if (!list.length) return "ذاتي فقط";
  return list.map((a) => raterTypeLabel(a)).join("، ");
}

function raterNotes(rater, questions) {
  const byId = Object.fromEntries(
    (questions || []).map((q) => [q.id, q.text]),
  );
  const general = String(rater?.comment || "").trim();
  const answerNotes = (rater?.answers || [])
    .filter((a) => String(a.comment || "").trim())
    .map((a) => ({
      question: byId[a.questionId] || "سؤال",
      comment: String(a.comment).trim(),
    }));
  return {
    general,
    answerNotes,
    hasAny: Boolean(general || answerNotes.length),
  };
}

function formatDateInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const NO_BRANCH = "__no_branch__";

function branchKeyOf(emp) {
  const name = String(emp?.branchName || "").trim();
  return name || NO_BRANCH;
}

function jobKeyOf(emp) {
  return String(emp?.jobTitle || "").trim();
}

function departmentKeyOf(emp) {
  return String(emp?.departmentName || "").trim();
}

function branchOptionLabel(key) {
  return key === NO_BRANCH ? "بدون فرع" : key;
}

function sortAr(values) {
  return [...values].sort((a, b) => a.localeCompare(b, "ar"));
}

function collectBranchOptions(people) {
  const keys = people.map((person) => person.branchKey ?? branchKeyOf(person));
  const named = sortAr(new Set(keys.filter((key) => key && key !== NO_BRANCH)));
  if (keys.includes(NO_BRANCH)) named.push(NO_BRANCH);
  return named;
}

function collectDepartmentOptions(people, branchFilter) {
  const pool = branchFilter
    ? people.filter(
        (person) => (person.branchKey ?? branchKeyOf(person)) === branchFilter,
      )
    : people;
  return sortAr(
    new Set(pool.map((person) => departmentKeyOf(person)).filter(Boolean)),
  );
}

function collectJobOptions(people, branchFilter, departmentFilter = "") {
  let pool = branchFilter
    ? people.filter(
        (person) => (person.branchKey ?? branchKeyOf(person)) === branchFilter,
      )
    : people;
  if (departmentFilter) {
    pool = pool.filter((person) => departmentKeyOf(person) === departmentFilter);
  }
  return sortAr(
    new Set(pool.map((person) => person.jobKey ?? jobKeyOf(person)).filter(Boolean)),
  );
}

function personMatchesQuery(emp, query) {
  if (!query) return true;
  return (
    (emp.name || "").toLowerCase().includes(query) ||
    (emp.fullName || "").toLowerCase().includes(query) ||
    (emp.code || "").toLowerCase().includes(query) ||
    (emp.employeeCode || "").toLowerCase().includes(query) ||
    (emp.jobTitle || "").toLowerCase().includes(query) ||
    (emp.branchName || "").toLowerCase().includes(query)
  );
}

function OrgFilters({
  branchValue,
  jobValue,
  branches,
  jobs,
  onBranch,
  onJob,
  departmentValue,
  departments,
  onDepartment,
}) {
  const showDepartment = Array.isArray(departments);
  const columnClass = showDepartment ? "col-md-4" : "col-md-6";
  return (
    <div className="row g-2 mb-2">
      <div className={columnClass}>
        <label className="form-label">الفرع</label>
        <select
          className="form-control"
          value={branchValue}
          onChange={(e) => onBranch(e.target.value)}
          aria-label="فلتر الفرع"
        >
          <option value="">كل الفروع</option>
          {branches.map((key) => (
            <option key={key} value={key}>
              {branchOptionLabel(key)}
            </option>
          ))}
        </select>
      </div>
      {showDepartment ? (
        <div className={columnClass}>
          <label className="form-label">الإدارة</label>
          <select
            className="form-control"
            value={departmentValue || ""}
            onChange={(e) => onDepartment(e.target.value)}
            aria-label="فلتر الإدارة"
          >
            <option value="">كل الإدارات</option>
            {departments.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className={columnClass}>
        <label className="form-label">الوظيفة</label>
        <select
          className="form-control"
          value={jobValue}
          onChange={(e) => onJob(e.target.value)}
          aria-label="فلتر الوظيفة"
        >
          <option value="">كل الوظائف</option>
          {jobs.map((title) => (
            <option key={title} value={title}>
              {title}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function questionTargetLabel(question, subjects) {
  const ids = Array.isArray(question?.targetEmployeeIds)
    ? question.targetEmployeeIds
    : [];
  if (!ids.length) return "لكل الموظفين";
  const names = ids
    .map((id) => {
      const subject = (subjects || []).find(
        (row) => String(row.employeeId || row.employee?.id) === String(id),
      );
      return subject?.employee?.fullName || "";
    })
    .filter(Boolean);
  if (!names.length) return `${ids.length} موظف`;
  if (names.length <= 3) return names.join("، ");
  return `${names.slice(0, 2).join("، ")} و${names.length - 2} آخرون`;
}

function questionRaterLabel(question, employeesById) {
  const ids = Array.isArray(question?.raterEmployeeIds)
    ? question.raterEmployeeIds
    : [];
  if (!ids.length) return "حسب الجمهور";
  const names = ids
    .map((id) => {
      const emp = employeesById?.get?.(String(id));
      return emp?.name || emp?.fullName || "";
    })
    .filter(Boolean);
  if (!names.length) return `${ids.length} مقيّم`;
  if (names.length <= 3) return `يقيّمه: ${names.join("، ")}`;
  return `يقيّمه: ${names.slice(0, 2).join("، ")} و${names.length - 2} آخرون`;
}

function QuestionSubjectPicker({
  employees,
  selectedIds,
  onChange,
  labelPrefix,
}) {
  const [query, setQuery] = useState("");
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  const q = query.trim().toLowerCase();
  const shown = (employees || []).filter((emp) => {
    if (!q) return true;
    return (
      personMatchesQuery(emp, q) ||
      departmentKeyOf(emp).toLowerCase().includes(q)
    );
  });
  const toggle = (employeeId) => {
    onChange(
      ids.includes(employeeId)
        ? ids.filter((id) => id !== employeeId)
        : [...ids, employeeId],
    );
  };

  return (
    <div className="hr-evaluations__question-targets">
      <div className="hr-evaluations__audience-row">
        <span className="hr-evaluations__audience-label">ينطبق على:</span>
        <label
          className={`hr-evaluations__audience-chip ${
            ids.length === 0 ? "is-checked" : ""
          }`}
        >
          <input
            type="checkbox"
            checked={ids.length === 0}
            onChange={() => onChange([])}
          />
          كل الموظفين
        </label>
        {ids.length > 0 ? (
          <span className="text-muted small">محدد لـ {ids.length} موظف</span>
        ) : null}
      </div>
      {!employees?.length ? (
        <p className="text-muted small mb-0">
          اختر الموظفين الخاضعين للتقييم أولاً لتخصيص هذا السؤال.
        </p>
      ) : (
        <>
          {employees.length > 8 ? (
            <input
              className="form-control form-control-sm mb-1"
              placeholder="بحث عن موظف لتخصيص السؤال"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          ) : null}
          <div className="hr-evaluations__multi-list">
            {shown.slice(0, 40).map((emp) => {
              const name = emp.name || emp.fullName;
              const checked = ids.includes(emp.id);
              return (
                <label
                  key={emp.id}
                  className={`hr-evaluations__check-item ${
                    checked ? "is-checked" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    aria-label={`${labelPrefix} ${name}`}
                    onChange={() => toggle(emp.id)}
                  />
                  <span>
                    {name}
                    <span className="text-muted">
                      {emp.jobTitle ? ` — ${emp.jobTitle}` : ""}
                      {emp.departmentName ? ` — ${emp.departmentName}` : ""}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function QuestionRaterPicker({ employees, selectedIds, onChange, labelPrefix }) {
  const [query, setQuery] = useState("");
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  const q = query.trim().toLowerCase();
  const shown = (employees || []).filter((emp) => personMatchesQuery(emp, q));
  const toggle = (employeeId) => {
    onChange(
      ids.includes(employeeId)
        ? ids.filter((id) => id !== employeeId)
        : [...ids, employeeId],
    );
  };

  return (
    <div className="hr-evaluations__question-targets">
      <div className="hr-evaluations__audience-row">
        <span className="hr-evaluations__audience-label">يقيّمه:</span>
        <label
          className={`hr-evaluations__audience-chip ${
            ids.length === 0 ? "is-checked" : ""
          }`}
        >
          <input
            type="checkbox"
            checked={ids.length === 0}
            onChange={() => onChange([])}
          />
          حسب الجمهور
        </label>
        {ids.length > 0 ? (
          <span className="text-muted small">
            هذا السؤال يقيّمه {ids.length} موظف فقط
          </span>
        ) : null}
      </div>
      <input
        className="form-control form-control-sm mb-1"
        placeholder="ابحث عن الموظف الذي سيقيم هذا السؤال"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {!q ? (
        <p className="text-muted small mb-0">
          ابحث بالاسم لإرسال السؤال لهذا الموظف.
        </p>
      ) : (
      <div className="hr-evaluations__multi-list">
        {shown.length === 0 ? (
          <p className="text-muted p-2 mb-0">لا يوجد موظفون مطابقون</p>
        ) : (
          shown.slice(0, 40).map((emp) => {
            const name = emp.name || emp.fullName;
            const checked = ids.includes(emp.id);
            return (
              <label
                key={emp.id}
                className={`hr-evaluations__check-item ${
                  checked ? "is-checked" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  aria-label={`${labelPrefix} ${name}`}
                  onChange={() => toggle(emp.id)}
                />
                <span>
                  {name}
                  <span className="text-muted">
                    {emp.jobTitle ? ` — ${emp.jobTitle}` : ""}
                    {emp.departmentName ? ` — ${emp.departmentName}` : ""}
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>
      )}
    </div>
  );
}

function subjectPickerEmployees(round, employeesById) {
  return (round?.subjects || [])
    .map((subject) => {
      const id = String(subject.employeeId || subject.employee?.id || "");
      const local = employeesById?.get?.(id);
      return {
        id,
        name: subject.employee?.fullName || local?.name || local?.fullName || "",
        fullName: subject.employee?.fullName || local?.fullName || "",
        jobTitle: subject.employee?.jobTitle || local?.jobTitle || "",
        departmentName: local?.departmentName || "",
      };
    })
    .filter((emp) => emp.id);
}

function employeeBranchBackendId(emp) {
  return String(emp?.branchBackendId || "");
}

function employeeDepartmentBackendId(emp) {
  return String(emp?.departmentBackendId || "");
}

function optionId(row) {
  return String(row?.backendId || row?.id || "");
}

function employeesInScope(employees, scope) {
  return employees.filter((emp) => {
    const branchId = employeeBranchBackendId(emp);
    const departmentId = employeeDepartmentBackendId(emp);
    if (scope.type === "branch") {
      if (!scope.branchId || branchId !== scope.branchId) return false;
      if (scope.departmentId && departmentId !== scope.departmentId) return false;
    } else if (scope.type === "department") {
      if (!scope.departmentId || departmentId !== scope.departmentId) return false;
      if (scope.branchId && branchId !== scope.branchId) return false;
    } else {
      return false;
    }
    if (scope.jobTitle && jobKeyOf(emp) !== scope.jobTitle) return false;
    return true;
  });
}

function roundTargetLabel(round) {
  const job = String(round?.targetJobTitle || "").trim();
  const jobSuffix = job ? ` — ${job}` : "";
  if (round?.targetType === "BRANCH") {
    const branch = round.targetBranch?.name || "—";
    const dept = round.targetDepartment?.name;
    return dept
      ? `فرع: ${branch} — إدارة: ${dept}${jobSuffix}`
      : `فرع: ${branch}${jobSuffix}`;
  }
  if (round?.targetType === "DEPARTMENT") {
    const dept = round.targetDepartment?.name || "—";
    const branch = round.targetBranch?.name;
    return branch
      ? `إدارة: ${dept} — فرع: ${branch}${jobSuffix}`
      : `إدارة: ${dept}${jobSuffix}`;
  }
  if (round?.targetType === "EMPLOYEES") {
    const count = Array.isArray(round.subjects) ? round.subjects.length : 0;
    return count
      ? `موظفون من أقسام أو وظائف مختلفة (${count})`
      : "موظفون من أقسام أو وظائف مختلفة";
  }
  const name =
    round?.targetEmployee?.fullName ||
    round?.subjects?.[0]?.employee?.fullName ||
    "—";
  return `موظف: ${name}`;
}

/** Default evaluation window: current calendar month. */
function getCurrentMonthDefaults(ref = new Date()) {
  const year = ref.getFullYear();
  const month = ref.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const monthLabel = start.toLocaleDateString("ar-EG", {
    month: "long",
    year: "numeric",
  });
  return {
    title: `تقييم الأداء 360 درجة — ${monthLabel}`,
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  };
}

function getMonthDefaultsOffset(monthOffset = 0, ref = new Date()) {
  const base = new Date(ref.getFullYear(), ref.getMonth() + monthOffset, 1);
  return getCurrentMonthDefaults(base);
}

export default function HrEvaluations() {
  const { employeesByBranch, departments, branches } = useData();

  const allEmployees = useMemo(
    () =>
      Object.values(employeesByBranch || {})
        .flat()
        .filter((e) => e.status !== "terminated"),
    [employeesByBranch],
  );

  const deptOptions = useMemo(
    () =>
      (departments || []).filter(
        (d) => d.isActive !== false && (d.backendId || d.id),
      ),
    [departments],
  );

  const branchOptions = useMemo(
    () =>
      (branches || []).filter(
        (b) => b.isActive !== false && (b.backendId || b.id),
      ),
    [branches],
  );

  const monthDefaults = useMemo(() => getCurrentMonthDefaults(), []);

  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState(monthDefaults.title);
  const [targetType, setTargetType] = useState("employee");
  const [targetEmployeeId, setTargetEmployeeId] = useState("");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [mixedSearch, setMixedSearch] = useState("");
  const [mixedBranchFilter, setMixedBranchFilter] = useState("");
  const [mixedDepartmentFilter, setMixedDepartmentFilter] = useState("");
  const [mixedJobFilter, setMixedJobFilter] = useState("");
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [targetJobTitle, setTargetJobTitle] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeBranchFilter, setEmployeeBranchFilter] = useState("");
  const [employeeJobFilter, setEmployeeJobFilter] = useState("");
  const [startDate, setStartDate] = useState(monthDefaults.startDate);
  const [endDate, setEndDate] = useState(monthDefaults.endDate);
  const [questions, setQuestions] = useState(() => buildDefaultQuestions());
  const [draftRaterType, setDraftRaterType] = useState("PEER");
  const [editingDraftQuestions, setEditingDraftQuestions] = useState(false);
  const [draftEditTitle, setDraftEditTitle] = useState("");
  const [draftEditStart, setDraftEditStart] = useState("");
  const [draftEditEnd, setDraftEditEnd] = useState("");
  const [draftEditQuestions, setDraftEditQuestions] = useState([]);
  const [showRepublish, setShowRepublish] = useState(false);
  const [republishTitle, setRepublishTitle] = useState("");
  const [republishStart, setRepublishStart] = useState("");
  const [republishEnd, setRepublishEnd] = useState("");
  const [showSpread, setShowSpread] = useState(false);
  const [spreadBranchIds, setSpreadBranchIds] = useState([]);
  const [spreadDepartmentIds, setSpreadDepartmentIds] = useState([]);
  const [spreadActivate, setSpreadActivate] = useState(false);
  const [showJobRaterPanel, setShowJobRaterPanel] = useState(false);
  const [jobRaterTitle, setJobRaterTitle] = useState("");
  const [jobRaterType, setJobRaterType] = useState("CUSTOM");
  const [jobRaterSameBranch, setJobRaterSameBranch] = useState(true);
  const [jobRaterRelated, setJobRaterRelated] = useState(true);

  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [raterDraft, setRaterDraft] = useState([]);
  const [raterSearch, setRaterSearch] = useState("");
  const [raterBranchFilter, setRaterBranchFilter] = useState("");
  const [raterJobFilter, setRaterJobFilter] = useState("");
  const [subjectBranchFilter, setSubjectBranchFilter] = useState("");
  const [subjectDepartmentFilter, setSubjectDepartmentFilter] = useState("");
  const [subjectJobFilter, setSubjectJobFilter] = useState("");

  const totalWeight = useMemo(
    () =>
      questions.reduce((s, q) => s + (Number(q.weightPercent) || 0), 0),
    [questions],
  );

  const allWeightsValid = useMemo(
    () =>
      Math.abs(totalWeight - 100) < 0.01 &&
      questions.some((q) => q.text.trim()),
    [questions, totalWeight],
  );

  const draftEditTotalWeight = useMemo(
    () =>
      draftEditQuestions.reduce(
        (s, q) => s + (Number(q.weightPercent) || 0),
        0,
      ),
    [draftEditQuestions],
  );

  const draftEditWeightsValid = useMemo(
    () =>
      Math.abs(draftEditTotalWeight - 100) < 0.01 &&
      draftEditQuestions.some((q) => q.text.trim()),
    [draftEditQuestions, draftEditTotalWeight],
  );

  const loadRounds = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await getEvaluationRounds();
      setRounds(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل جولات التقييم"));
      setRounds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRounds();
  }, [loadRounds]);

  const loadRoundDetail = useCallback(async (id) => {
    if (!id) {
      setSelectedRound(null);
      return;
    }
    setDetailLoading(true);
    setError("");
    try {
      const row = await getEvaluationRound(id);
      setSelectedRound(row);
      setSelectedRoundId(id);
      setEditingDraftQuestions(false);
      setShowRepublish(false);
      setShowSpread(false);
      setShowJobRaterPanel(false);
      setSubjectBranchFilter("");
      setSubjectDepartmentFilter("");
      setSubjectJobFilter("");
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل تفاصيل الجولة"));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const employeesById = useMemo(() => {
    const map = new Map();
    allEmployees.forEach((emp) => map.set(String(emp.id), emp));
    return map;
  }, [allEmployees]);

  const employeeBranchOptions = useMemo(
    () => collectBranchOptions(allEmployees),
    [allEmployees],
  );

  const employeeJobOptions = useMemo(
    () => collectJobOptions(allEmployees, employeeBranchFilter),
    [allEmployees, employeeBranchFilter],
  );

  const keepJobIfValid = (people, nextBranch, setJob) => {
    setJob((current) =>
      current && collectJobOptions(people, nextBranch).includes(current)
        ? current
        : "",
    );
  };

  const filteredEmployees = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    const narrowed = Boolean(q || employeeBranchFilter || employeeJobFilter);
    const matched = allEmployees.filter((emp) => {
      if (employeeBranchFilter && branchKeyOf(emp) !== employeeBranchFilter) {
        return false;
      }
      if (employeeJobFilter && jobKeyOf(emp) !== employeeJobFilter) {
        return false;
      }
      return personMatchesQuery(emp, q);
    });
    return {
      shown: narrowed ? matched.slice(0, 200) : matched.slice(0, 40),
      total: matched.length,
    };
  }, [allEmployees, employeeSearch, employeeBranchFilter, employeeJobFilter]);

  const selectedTarget = useMemo(
    () => allEmployees.find((emp) => emp.id === targetEmployeeId) || null,
    [allEmployees, targetEmployeeId],
  );

  const mixedDepartmentOptions = useMemo(
    () => collectDepartmentOptions(allEmployees, mixedBranchFilter),
    [allEmployees, mixedBranchFilter],
  );

  const mixedJobOptions = useMemo(
    () =>
      collectJobOptions(allEmployees, mixedBranchFilter, mixedDepartmentFilter),
    [allEmployees, mixedBranchFilter, mixedDepartmentFilter],
  );

  const mixedEmployees = useMemo(() => {
    const q = mixedSearch.trim().toLowerCase();
    const matched = allEmployees.filter((emp) => {
      if (mixedBranchFilter && branchKeyOf(emp) !== mixedBranchFilter) {
        return false;
      }
      if (mixedDepartmentFilter && departmentKeyOf(emp) !== mixedDepartmentFilter) {
        return false;
      }
      if (mixedJobFilter && jobKeyOf(emp) !== mixedJobFilter) {
        return false;
      }
      return personMatchesQuery(emp, q);
    });
    const narrowed = Boolean(
      q || mixedBranchFilter || mixedDepartmentFilter || mixedJobFilter,
    );
    return {
      matched,
      shown: narrowed ? matched.slice(0, 200) : matched.slice(0, 40),
    };
  }, [
    allEmployees,
    mixedSearch,
    mixedBranchFilter,
    mixedDepartmentFilter,
    mixedJobFilter,
  ]);

  const selectedMixedEmployees = useMemo(
    () =>
      selectedEmployeeIds
        .map((id) => allEmployees.find((emp) => emp.id === id))
        .filter(Boolean),
    [allEmployees, selectedEmployeeIds],
  );

  const scopeBaseEmployees = useMemo(() => {
    if (targetType === "employee") return [];
    if (targetType === "branch") {
      if (!selectedBranchIds.length) return [];
      const seen = new Set();
      const rows = [];
      selectedBranchIds.forEach((branchId) => {
        employeesInScope(allEmployees, {
          type: "branch",
          branchId,
          departmentId: "",
          jobTitle: "",
        }).forEach((emp) => {
          if (seen.has(emp.id)) return;
          seen.add(emp.id);
          rows.push(emp);
        });
      });
      return rows;
    }
    if (!selectedDepartmentIds.length) return [];
    const seen = new Set();
    const rows = [];
    selectedDepartmentIds.forEach((departmentId) => {
      employeesInScope(allEmployees, {
        type: "department",
        departmentId,
        branchId: "",
        jobTitle: "",
      }).forEach((emp) => {
        if (seen.has(emp.id)) return;
        seen.add(emp.id);
        rows.push(emp);
      });
    });
    return rows;
  }, [allEmployees, targetType, selectedBranchIds, selectedDepartmentIds]);

  const scopeJobOptions = useMemo(
    () => sortAr(new Set(scopeBaseEmployees.map(jobKeyOf).filter(Boolean))),
    [scopeBaseEmployees],
  );

  const scopedEmployees = useMemo(
    () =>
      targetJobTitle
        ? scopeBaseEmployees.filter((emp) => jobKeyOf(emp) === targetJobTitle)
        : scopeBaseEmployees,
    [scopeBaseEmployees, targetJobTitle],
  );

  const questionCandidateEmployees = useMemo(() => {
    if (targetType === "employee") return selectedTarget ? [selectedTarget] : [];
    if (targetType === "employees") return selectedMixedEmployees;
    return scopedEmployees;
  }, [targetType, selectedTarget, selectedMixedEmployees, scopedEmployees]);

  const resetForm = () => {
    const defaults = getCurrentMonthDefaults();
    setTitle(defaults.title);
    setTargetType("employee");
    setTargetEmployeeId("");
    setSelectedEmployeeIds([]);
    setMixedSearch("");
    setMixedBranchFilter("");
    setMixedDepartmentFilter("");
    setMixedJobFilter("");
    setSelectedBranchIds([]);
    setSelectedDepartmentIds([]);
    setTargetJobTitle("");
    setEmployeeSearch("");
    setEmployeeBranchFilter("");
    setEmployeeJobFilter("");
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
    setQuestions(buildDefaultQuestions());
    setShowForm(false);
  };

  const applyMonthPreset = (offset) => {
    const defaults = getMonthDefaultsOffset(offset);
    setTitle(defaults.title);
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
  };

  const toggleSelectedId = (list, setList, id) => {
    setList((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const buildQuestionPayload = (sourceQuestions, allowedEmployees) => {
    const allowed = new Set((allowedEmployees || []).map((emp) => emp.id));
    return sourceQuestions
      .filter((q) => q.text.trim())
      .map((q, index) => ({
        audiences: Array.isArray(q.audiences) ? q.audiences : [],
        targetEmployeeIds: (
          Array.isArray(q.targetEmployeeIds) ? q.targetEmployeeIds : []
        ).filter((id) => !allowed.size || allowed.has(id)),
        raterEmployeeIds: Array.isArray(q.raterEmployeeIds)
          ? q.raterEmployeeIds
          : [],
        text: q.text.trim(),
        weightPercent: Number(q.weightPercent),
        maxScore: Number(q.maxScore) || 5,
        sortOrder: index,
      }));
  };

  const validateQuestions = (sourceQuestions) => {
    const filled = sourceQuestions.filter((q) => q.text.trim());
    if (!filled.length) return "أضف سؤالاً واحداً على الأقل";
    if (filled.some((q) => !Number(q.weightPercent))) {
      return "أكمل أوزان جميع الأسئلة";
    }
    const weightSum = filled.reduce((s, q) => s + Number(q.weightPercent), 0);
    if (Math.abs(weightSum - 100) > 0.01) {
      return `مجموع أوزان جميع الأسئلة يجب أن يساوي 100% (الحالي: ${weightSum}%)`;
    }
    return "";
  };

  const updateQuestion = (index, field, value) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, [field]: value } : q)),
    );
  };

  const toggleAudience = (index, audienceKey) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== index) return q;
        const current = Array.isArray(q.audiences) ? q.audiences : [];
        const next = current.includes(audienceKey)
          ? current.filter((a) => a !== audienceKey)
          : [...current, audienceKey];
        return { ...q, audiences: next };
      }),
    );
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, emptyQuestion()]);
  };

  const removeQuestion = (index) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!title.trim()) {
      setError("أدخل عنوان تقييم الأداء 360 درجة");
      return;
    }
    if (targetType === "employee" && !targetEmployeeId) {
      setError("اختر الموظف");
      return;
    }
    if (targetType === "employees" && selectedEmployeeIds.length === 0) {
      setError("اختر موظفاً واحداً على الأقل من أي قسم أو وظيفة");
      return;
    }
    if (targetType === "branch" && selectedBranchIds.length === 0) {
      setError("اختر فرعاً واحداً على الأقل");
      return;
    }
    if (targetType === "department" && selectedDepartmentIds.length === 0) {
      setError("اختر إدارة واحدة على الأقل");
      return;
    }
    if (
      targetType !== "employee" &&
      targetType !== "employees" &&
      scopedEmployees.length === 0
    ) {
      setError("لا يوجد موظفون مطابقون للنطاق المحدد");
      return;
    }
    const questionError = validateQuestions(questions);
    if (questionError) {
      setError(questionError);
      return;
    }

    const questionPayload = buildQuestionPayload(
      questions,
      questionCandidateEmployees,
    );
    const basePayload = {
      title: title.trim(),
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      questions: questionPayload,
    };

    setSubmitting(true);
    try {
      if (targetType === "employee") {
        const created = await createEvaluationRound({
          ...basePayload,
          targetType: "employee",
          targetEmployeeId,
        });
        setSuccess("تم إنشاء جولة تقييم الأداء 360 درجة كمسودة مع اقتراح المقيمين");
        resetForm();
        await loadRounds();
        if (created?.id) await loadRoundDetail(created.id);
        return;
      }

      if (targetType === "employees") {
        const created = await createEvaluationRound({
          ...basePayload,
          targetType: "employees",
          targetEmployeeIds: selectedEmployeeIds,
        });
        setSuccess(
          `تم إنشاء مسودة واحدة بنفس الأسئلة لـ ${selectedEmployeeIds.length} موظف من أقسام أو وظائف مختلفة`,
        );
        resetForm();
        await loadRounds();
        if (created?.id) await loadRoundDetail(created.id);
        return;
      }

      const targets =
        targetType === "branch"
          ? selectedBranchIds.map((branchId) => {
              const branch = branchOptions.find(
                (b) => optionId(b) === branchId,
              );
              return {
                targetType: "branch",
                targetBranchId: branchId,
                targetJobTitle: targetJobTitle || undefined,
                label: branch?.name || branchId,
              };
            })
          : selectedDepartmentIds.map((departmentId) => {
              const dept = deptOptions.find((d) => optionId(d) === departmentId);
              return {
                targetType: "department",
                targetDepartmentId: departmentId,
                targetJobTitle: targetJobTitle || undefined,
                label: dept?.name || departmentId,
              };
            });

      const createdIds = [];
      const failed = [];
      for (const target of targets) {
        try {
          const created = await createEvaluationRound({
            ...basePayload,
            title: `${basePayload.title} — ${target.label}`,
            targetType: target.targetType,
            targetBranchId: target.targetBranchId,
            targetDepartmentId: target.targetDepartmentId,
            targetJobTitle: target.targetJobTitle,
          });
          if (created?.id) createdIds.push(created.id);
        } catch (err) {
          failed.push(
            `${target.label}: ${getApiErrorMessage(err, "فشل الإنشاء")}`,
          );
        }
      }

      if (!createdIds.length) {
        setError(
          failed.length
            ? failed.join("\n")
            : "تعذر إنشاء أي جولة للنطاق المحدد",
        );
        return;
      }

      setSuccess(
        failed.length
          ? `تم إنشاء ${createdIds.length} مسودة بنفس الأسئلة. بعض النطاقات فشلت:\n${failed.join("\n")}`
          : `تم إنشاء ${createdIds.length} مسودة بنفس الأسئلة والتواريخ للمقيمين المقترحين`,
      );
      resetForm();
      await loadRounds();
      await loadRoundDetail(createdIds[0]);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إنشاء الجولة"));
    } finally {
      setSubmitting(false);
    }
  };

  const startEditDraftQuestions = () => {
    if (!selectedRound || selectedRound.status !== "DRAFT") return;
    setDraftEditTitle(selectedRound.title || "");
    setDraftEditStart(
      selectedRound.startDate
        ? String(selectedRound.startDate).slice(0, 10)
        : "",
    );
    setDraftEditEnd(
      selectedRound.endDate ? String(selectedRound.endDate).slice(0, 10) : "",
    );
    setDraftEditQuestions(
      (selectedRound.questions || []).map((q) => ({
        text: q.text || "",
        weightPercent: String(q.weightPercent ?? ""),
        maxScore: q.maxScore ?? 5,
        audiences: Array.isArray(q.audiences) ? [...q.audiences] : [],
        targetEmployeeIds: Array.isArray(q.targetEmployeeIds)
          ? [...q.targetEmployeeIds]
          : [],
        raterEmployeeIds: Array.isArray(q.raterEmployeeIds)
          ? [...q.raterEmployeeIds]
          : [],
      })),
    );
    setEditingDraftQuestions(true);
  };

  const updateDraftEditQuestion = (index, field, value) => {
    setDraftEditQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, [field]: value } : q)),
    );
  };

  const toggleDraftEditAudience = (index, key) => {
    setDraftEditQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== index) return q;
        const current = Array.isArray(q.audiences) ? q.audiences : [];
        const next = current.includes(key)
          ? current.filter((a) => a !== key)
          : [...current, key];
        return { ...q, audiences: next };
      }),
    );
  };

  const addDraftEditQuestion = () => {
    setDraftEditQuestions((prev) => [
      ...prev,
      {
        text: "",
        weightPercent: "",
        maxScore: 5,
        audiences: [],
        targetEmployeeIds: [],
        raterEmployeeIds: [],
      },
    ]);
  };

  const removeDraftEditQuestion = (index) => {
    setDraftEditQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveDraftQuestions = async () => {
    if (!selectedRound?.id) return;
    setError("");
    setSuccess("");
    const questionError = validateQuestions(draftEditQuestions);
    if (questionError) {
      setError(questionError);
      return;
    }
    if (!draftEditTitle.trim()) {
      setError("أدخل عنوان الجولة");
      return;
    }
    setSubmitting(true);
    try {
      const updated = await updateEvaluationRound(selectedRound.id, {
        title: draftEditTitle.trim(),
        startDate: draftEditStart || undefined,
        endDate: draftEditEnd || undefined,
        questions: buildQuestionPayload(
          draftEditQuestions,
          subjectPickerEmployees(selectedRound, employeesById),
        ),
      });
      setSuccess("تم تحديث أسئلة وتواريخ المسودة");
      setEditingDraftQuestions(false);
      setSelectedRound(updated);
      await loadRounds();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث المسودة"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (id) => {
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      await activateEvaluationRound(id);
      setSuccess("تم تفعيل الجولة — يمكن للمقيمين البدء");
      await loadRounds();
      await loadRoundDetail(id);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تفعيل الجولة"));
    } finally {
      setSubmitting(false);
    }
  };

  const openRepublishPanel = () => {
    if (!selectedRound) return;
    const next = getMonthDefaultsOffset(1);
    setRepublishTitle(next.title);
    setRepublishStart(next.startDate);
    setRepublishEnd(next.endDate);
    setShowRepublish(true);
    setShowSpread(false);
    setShowJobRaterPanel(false);
    setEditingDraftQuestions(false);
  };

  const applyRepublishMonthPreset = (offset) => {
    const preset = getMonthDefaultsOffset(offset);
    setRepublishTitle(preset.title);
    setRepublishStart(preset.startDate);
    setRepublishEnd(preset.endDate);
  };

  const handleRepublish = async () => {
    if (!selectedRound?.id) return;
    setError("");
    setSuccess("");
    if (!republishTitle.trim()) {
      setError("أدخل عنوان الجولة الجديدة");
      return;
    }
    setSubmitting(true);
    try {
      const created = await cloneEvaluationRound(selectedRound.id, {
        title: republishTitle.trim(),
        startDate: republishStart || undefined,
        endDate: republishEnd || undefined,
      });
      setShowRepublish(false);
      setSuccess(
        "تم إنشاء مسودة جديدة بنفس الأسئلة والنطاق — يمكنك تعديل الأسئلة ثم التفعيل",
      );
      await loadRounds();
      if (created?.id) {
        await loadRoundDetail(created.id);
        // Open draft editor so HR can tweak questions for the new month
        setDraftEditTitle(created.title || republishTitle.trim());
        setDraftEditStart(
          created.startDate
            ? String(created.startDate).slice(0, 10)
            : republishStart,
        );
        setDraftEditEnd(
          created.endDate ? String(created.endDate).slice(0, 10) : republishEnd,
        );
        setDraftEditQuestions(
          (created.questions || []).map((q) => ({
            text: q.text || "",
            weightPercent: String(q.weightPercent ?? ""),
            maxScore: q.maxScore ?? 5,
            audiences: Array.isArray(q.audiences) ? [...q.audiences] : [],
            targetEmployeeIds: Array.isArray(q.targetEmployeeIds)
              ? [...q.targetEmployeeIds]
              : [],
            raterEmployeeIds: Array.isArray(q.raterEmployeeIds)
              ? [...q.raterEmployeeIds]
              : [],
          })),
        );
        setEditingDraftQuestions(true);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إعادة نشر الجولة"));
    } finally {
      setSubmitting(false);
    }
  };

  const spreadTargetMode = useMemo(() => {
    if (!selectedRound) return "branch";
    if (selectedRound.targetType === "DEPARTMENT") return "department";
    return "branch";
  }, [selectedRound]);

  const spreadBranchOptions = useMemo(() => {
    const currentId =
      selectedRound?.targetBranchId ||
      selectedRound?.targetBranch?.id ||
      "";
    return branchOptions.filter((b) => optionId(b) !== currentId);
  }, [branchOptions, selectedRound]);

  const spreadDepartmentOptions = useMemo(() => {
    const currentId =
      selectedRound?.targetDepartmentId ||
      selectedRound?.targetDepartment?.id ||
      "";
    return deptOptions.filter((d) => optionId(d) !== currentId);
  }, [deptOptions, selectedRound]);

  const openSpreadPanel = () => {
    if (!selectedRound) return;
    setSpreadBranchIds([]);
    setSpreadDepartmentIds([]);
    setSpreadActivate(false);
    setShowSpread(true);
    setShowRepublish(false);
    setShowJobRaterPanel(false);
    setEditingDraftQuestions(false);
  };

  const handleSpread = async () => {
    if (!selectedRound?.id) return;
    setError("");
    setSuccess("");
    const ids =
      spreadTargetMode === "department"
        ? spreadDepartmentIds
        : spreadBranchIds;
    if (!ids.length) {
      setError(
        spreadTargetMode === "department"
          ? "اختر إدارة واحدة واحدة على الأقل"
          : "اختر فرعاً واحداً على الأقل",
      );
      return;
    }
    setSubmitting(true);
    try {
      const result = await spreadEvaluationRound(selectedRound.id, {
        targetBranchIds:
          spreadTargetMode === "branch" ? spreadBranchIds : undefined,
        targetDepartmentIds:
          spreadTargetMode === "department" ? spreadDepartmentIds : undefined,
        activate: spreadActivate,
        startDate: selectedRound.startDate
          ? String(selectedRound.startDate).slice(0, 10)
          : undefined,
        endDate: selectedRound.endDate
          ? String(selectedRound.endDate).slice(0, 10)
          : undefined,
      });
      const createdCount = result?.createdCount || result?.created?.length || 0;
      const failed = result?.failed || [];
      setShowSpread(false);
      setSuccess(
        failed.length
          ? `تم إنشاء ${createdCount} جولة بنفس الأسئلة والشهر. فشل: ${failed
              .map((f) => `${f.label}: ${f.message}`)
              .join(" | ")}`
          : spreadActivate
            ? `تم إرسال وتفعيل التقييم لـ ${createdCount} نطاق بنفس الأسئلة والشهر`
            : `تم إنشاء ${createdCount} مسودة بنفس الأسئلة والشهر — راجعها ثم فعّلها`,
      );
      await loadRounds();
      const firstId = result?.created?.[0]?.id;
      if (firstId) await loadRoundDetail(firstId);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إرسال التقييم للفروع"));
    } finally {
      setSubmitting(false);
    }
  };

  const jobTitleOptions = useMemo(
    () => sortAr(new Set(allEmployees.map(jobKeyOf).filter(Boolean))),
    [allEmployees],
  );

  const openJobRaterPanel = () => {
    setJobRaterTitle("");
    setJobRaterType("CUSTOM");
    setJobRaterSameBranch(true);
    setJobRaterRelated(true);
    setShowJobRaterPanel(true);
    setShowSpread(false);
    setShowRepublish(false);
    setEditingDraftQuestions(false);
  };

  const handleAddRatersByJobTitle = async () => {
    if (!selectedRound?.id) return;
    setError("");
    setSuccess("");
    if (!jobRaterTitle.trim()) {
      setError("اختر المسمى الوظيفي");
      return;
    }
    setSubmitting(true);
    try {
      const result = await addEvaluationRatersByJobTitle(selectedRound.id, {
        jobTitle: jobRaterTitle.trim(),
        raterType: jobRaterType,
        sameBranchOnly: jobRaterSameBranch,
        applyToRelatedRounds: jobRaterRelated,
      });
      setShowJobRaterPanel(false);
      setSuccess(
        `تمت إضافة ${result?.addedCount || 0} مقيم بالمسمى «${jobRaterTitle}» عبر ${result?.roundsAffected || 1} جولة (${result?.subjectsAffected || 0} موظف خاضع للتقييم)`,
      );
      if (result?.round) {
        setSelectedRound(result.round);
        setSelectedRoundId(result.round.id);
      } else {
        await loadRoundDetail(selectedRound.id);
      }
      await loadRounds();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إضافة المقيمين بالمسمى الوظيفي"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async (id) => {
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      await closeEvaluationRound(id);
      setSuccess("تم إغلاق الجولة وحساب النتائج النهائية");
      await loadRounds();
      await loadRoundDetail(id);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إغلاق الجولة"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, status) => {
    const closed = status === "CLOSED";
    const message = closed
      ? "حذف هذا التقييم المغلق؟ سيتم حذف النتائج والإجابات ولا يمكن التراجع."
      : "حذف هذه المسودة؟";
    if (!window.confirm(message)) return;
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      await deleteEvaluationRound(id);
      setSuccess(closed ? "تم حذف التقييم المغلق" : "تم حذف المسودة");
      if (selectedRoundId === id) {
        setSelectedRoundId(null);
        setSelectedRound(null);
      }
      await loadRounds();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حذف الجولة"));
    } finally {
      setSubmitting(false);
    }
  };

  const startEditRaters = (subject) => {
    setEditingSubjectId(subject.id);
    setRaterDraft(
      (subject.raters || []).map((r) => ({
        employeeId: r.raterId || r.rater?.id,
        raterType: r.raterType || "PEER",
        name: r.rater?.fullName || "",
        code: r.rater?.employeeCode || "",
      })),
    );
    setDraftRaterType("PEER");
    setRaterSearch("");
    setRaterBranchFilter("");
    setRaterJobFilter("");
  };

  const removeRaterDraft = (employeeId, raterType) => {
    setRaterDraft((prev) =>
      prev.filter(
        (r) => !(r.employeeId === employeeId && r.raterType === raterType),
      ),
    );
  };

  const addRaterDraft = (emp, raterType) => {
    const employeeId = emp.id;
    const subject = selectedRound?.subjects?.find(
      (s) => s.id === editingSubjectId,
    );
    const subjectEmpId = subject?.employeeId || subject?.employee?.id;
    const type = raterType || draftRaterType;
    if (type === "SELF" && String(employeeId) !== String(subjectEmpId)) {
      setError("التقييم الذاتي للموظف الخاضع للتقييم فقط");
      return;
    }
    if (type !== "SELF" && String(employeeId) === String(subjectEmpId)) {
      setError("لا يمكن إضافة الموظف كمقيم غير ذاتي على نفسه");
      return;
    }
    setRaterDraft((prev) => {
      const without = prev.filter((r) => r.employeeId !== employeeId);
      return [
        ...without,
        {
          employeeId,
          raterType: type,
          name: emp.name || emp.fullName || "",
          code: emp.code || emp.employeeCode || "",
        },
      ];
    });
  };

  const saveRaters = async (subjectId) => {
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const updated = await setEvaluationSubjectRaters(
        subjectId,
        raterDraft.map((r) => ({
          employeeId: r.employeeId,
          raterType: r.raterType,
        })),
      );
      setSuccess("تم تحديث قائمة المقيمين");
      setEditingSubjectId(null);
      setSelectedRound(updated);
      await loadRounds();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحديث المقيمين"));
    } finally {
      setSubmitting(false);
    }
  };

  const raterPool = useMemo(() => {
    const subject = selectedRound?.subjects?.find(
      (s) => s.id === editingSubjectId,
    );
    const subjectEmpId = subject?.employeeId || subject?.employee?.id;
    if (draftRaterType === "SELF") {
      return allEmployees.filter((e) => String(e.id) === String(subjectEmpId));
    }
    return allEmployees.filter((e) => String(e.id) !== String(subjectEmpId));
  }, [allEmployees, editingSubjectId, selectedRound, draftRaterType]);

  const raterBranchOptions = useMemo(
    () => collectBranchOptions(raterPool),
    [raterPool],
  );

  const raterJobOptions = useMemo(
    () => collectJobOptions(raterPool, raterBranchFilter),
    [raterPool, raterBranchFilter],
  );

  const raterPickList = useMemo(() => {
    const q = raterSearch.trim().toLowerCase();
    const narrowed = Boolean(q || raterBranchFilter || raterJobFilter);
    const matched = raterPool.filter((emp) => {
      if (raterBranchFilter && branchKeyOf(emp) !== raterBranchFilter) {
        return false;
      }
      if (raterJobFilter && jobKeyOf(emp) !== raterJobFilter) return false;
      return personMatchesQuery(emp, q);
    });
    return {
      shown: narrowed ? matched.slice(0, 200) : matched.slice(0, 50),
      total: matched.length,
    };
  }, [raterPool, raterSearch, raterBranchFilter, raterJobFilter]);

  const roundSubjects = useMemo(() => {
    return (selectedRound?.subjects || []).map((subject) => {
      const local = employeesById.get(
        String(subject.employeeId || subject.employee?.id),
      );
      return {
        subject,
        branchKey: local ? branchKeyOf(local) : "",
        departmentKey: String(local?.departmentName || "").trim(),
        jobKey: String(subject.employee?.jobTitle || local?.jobTitle || "").trim(),
        branchName: local?.branchName || "",
      };
    });
  }, [selectedRound, employeesById]);

  const subjectBranchOptions = useMemo(
    () => collectBranchOptions(roundSubjects),
    [roundSubjects],
  );

  const subjectDepartmentOptions = useMemo(() => {
    const pool = subjectBranchFilter
      ? roundSubjects.filter((row) => row.branchKey === subjectBranchFilter)
      : roundSubjects;
    return sortAr(
      new Set(pool.map((row) => row.departmentKey).filter(Boolean)),
    );
  }, [roundSubjects, subjectBranchFilter]);

  const subjectJobOptions = useMemo(() => {
    const pool = roundSubjects.filter((row) => {
      if (subjectBranchFilter && row.branchKey !== subjectBranchFilter) return false;
      if (subjectDepartmentFilter && row.departmentKey !== subjectDepartmentFilter) {
        return false;
      }
      return true;
    });
    return sortAr(new Set(pool.map((row) => row.jobKey).filter(Boolean)));
  }, [roundSubjects, subjectBranchFilter, subjectDepartmentFilter]);

  const visibleSubjects = useMemo(() => {
    return roundSubjects.filter((row) => {
      if (subjectBranchFilter && row.branchKey !== subjectBranchFilter) {
        return false;
      }
      if (
        subjectDepartmentFilter &&
        row.departmentKey !== subjectDepartmentFilter
      ) {
        return false;
      }
      if (subjectJobFilter && row.jobKey !== subjectJobFilter) return false;
      return true;
    });
  }, [
    roundSubjects,
    subjectBranchFilter,
    subjectDepartmentFilter,
    subjectJobFilter,
  ]);

  return (
    <div className="hr-evaluations" dir="rtl">
      <div className="ess-home-header">
        <div>
          <h1 className="ess-home-title">تقييم الأداء 360 درجة</h1>
          <p className="ess-home-subtitle">
            جولة تقييم 360 درجة: الموظف يقيّم نفسه في كل الأسئلة، والآخرون حسب
            الجمهور المحدد لكل سؤال. الملاحظات تظهر للمقيم ولمن فتح الجولة
            بالاسم، وللموظف الخاضع للتقييم بدون اسم الكاتب.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            if (!showForm) {
              const defaults = getCurrentMonthDefaults();
              setTitle(defaults.title);
              setStartDate(defaults.startDate);
              setEndDate(defaults.endDate);
            }
            setShowForm((v) => !v);
          }}
        >
          {showForm ? "إخفاء النموذج" : "جولة تقييم جديدة"}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <form className="hr-evaluations__panel" onSubmit={handleCreate}>
          <h2 className="hr-evaluations__panel-title">
            إنشاء جولة تقييم أداء 360 درجة
          </h2>

          <div className="mb-3">
            <label className="form-label">عنوان الجولة</label>
            <input
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="مثال: تقييم الأداء 360 درجة — أغسطس 2026"
            />
            <div className="hr-evaluations__month-presets">
              <span className="form-text mb-0">تعيين شهر سريع:</span>
              {[0, 1, 2].map((offset) => {
                const preset = getMonthDefaultsOffset(offset);
                const label =
                  offset === 0
                    ? "هذا الشهر"
                    : offset === 1
                      ? "الشهر القادم"
                      : "بعد شهرين";
                return (
                  <button
                    key={offset}
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => applyMonthPreset(offset)}
                    title={preset.title}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="form-text">
              يمكن اختيار شهر قادم قبل بدايته — نفس الأسئلة تُطبَّق على كل
              الفروع/الإدارات المحددة
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="form-label">نوع الهدف</label>
              <select
                className="form-control"
                value={targetType}
                onChange={(e) => {
                  const next = e.target.value;
                  setTargetType(next);
                  setSelectedBranchIds([]);
                  setSelectedDepartmentIds([]);
                  setTargetJobTitle("");
                  setTargetEmployeeId("");
                  setSelectedEmployeeIds([]);
                }}
              >
                <option value="employee">موظف واحد</option>
                <option value="employees">
                  موظفون من أقسام أو وظائف مختلفة
                </option>
                <option value="branch">فروع (واحد أو أكثر)</option>
                <option value="department">إدارات (واحدة أو أكثر)</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">من تاريخ</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">إلى تاريخ</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {targetType === "employee" ? (
            <div className="mb-3">
              <label className="form-label">اختر الموظف</label>
              <OrgFilters
                branchValue={employeeBranchFilter}
                jobValue={employeeJobFilter}
                branches={employeeBranchOptions}
                jobs={employeeJobOptions}
                onBranch={(value) => {
                  setEmployeeBranchFilter(value);
                  keepJobIfValid(allEmployees, value, setEmployeeJobFilter);
                }}
                onJob={setEmployeeJobFilter}
              />
              <input
                className="form-control mb-2"
                placeholder="بحث بالاسم أو الكود..."
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
              />
              {selectedTarget ? (
                <div className="hr-evaluations__selected-target">
                  المحدد:{" "}
                  <strong>
                    {selectedTarget.name || selectedTarget.fullName}
                  </strong>
                  <span className="text-muted">
                    {" "}
                    {selectedTarget.code || selectedTarget.employeeCode}
                    {selectedTarget.jobTitle
                      ? ` — ${selectedTarget.jobTitle}`
                      : ""}
                    {selectedTarget.branchName
                      ? ` — ${selectedTarget.branchName}`
                      : ""}
                  </span>
                </div>
              ) : null}
              <p className="text-muted hr-evaluations__filter-count">
                ظاهر {filteredEmployees.shown.length} من {filteredEmployees.total}{" "}
                موظف
              </p>
              <div className="hr-evaluations__pick-list">
                {filteredEmployees.shown.length === 0 ? (
                  <p className="text-muted p-2 mb-0">
                    لا يوجد موظفون مطابقون للفلاتر
                  </p>
                ) : (
                  filteredEmployees.shown.map((emp) => (
                    <button
                      type="button"
                      key={emp.id}
                      className={`hr-evaluations__pick-item ${
                        targetEmployeeId === emp.id ? "is-selected" : ""
                      }`}
                      onClick={() => setTargetEmployeeId(emp.id)}
                    >
                      <strong>{emp.name || emp.fullName}</strong>
                      <span className="text-muted">
                        {emp.code || emp.employeeCode}
                        {emp.jobTitle ? ` — ${emp.jobTitle}` : ""}
                        {emp.branchName ? ` — ${emp.branchName}` : ""}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : targetType === "employees" ? (
            <div className="mb-3">
              <label className="form-label">
                اختر الموظفين — نفس الأسئلة للجميع حتى لو اختلف القسم أو الوظيفة
              </label>
              <OrgFilters
                branchValue={mixedBranchFilter}
                departmentValue={mixedDepartmentFilter}
                jobValue={mixedJobFilter}
                branches={employeeBranchOptions}
                departments={mixedDepartmentOptions}
                jobs={mixedJobOptions}
                onBranch={(value) => {
                  const nextDepartments = collectDepartmentOptions(
                    allEmployees,
                    value,
                  );
                  const nextDepartment =
                    mixedDepartmentFilter &&
                    nextDepartments.includes(mixedDepartmentFilter)
                      ? mixedDepartmentFilter
                      : "";
                  const nextJobs = collectJobOptions(
                    allEmployees,
                    value,
                    nextDepartment,
                  );
                  setMixedBranchFilter(value);
                  setMixedDepartmentFilter(nextDepartment);
                  setMixedJobFilter(
                    mixedJobFilter && nextJobs.includes(mixedJobFilter)
                      ? mixedJobFilter
                      : "",
                  );
                }}
                onDepartment={(value) => {
                  setMixedDepartmentFilter(value);
                  setMixedJobFilter((current) =>
                    current &&
                    collectJobOptions(
                      allEmployees,
                      mixedBranchFilter,
                      value,
                    ).includes(current)
                      ? current
                      : "",
                  );
                }}
                onJob={setMixedJobFilter}
              />
              <input
                className="form-control mb-2"
                placeholder="بحث بالاسم أو الكود..."
                value={mixedSearch}
                onChange={(e) => setMixedSearch(e.target.value)}
              />
              <p className="text-muted hr-evaluations__filter-count">
                محدد {selectedMixedEmployees.length} موظف — ظاهر{" "}
                {mixedEmployees.shown.length} من {mixedEmployees.matched.length}
              </p>
              {selectedMixedEmployees.length > 0 && (
                <div className="hr-evaluations__multi-list mb-2">
                  {selectedMixedEmployees.map((emp) => (
                    <label
                      key={emp.id}
                      className="hr-evaluations__check-item is-checked"
                    >
                      <input
                        type="checkbox"
                        checked
                        onChange={() =>
                          setSelectedEmployeeIds((prev) =>
                            prev.filter((id) => id !== emp.id),
                          )
                        }
                      />
                      <span>
                        <strong>{emp.name || emp.fullName}</strong>
                        <span className="text-muted">
                          {" "}
                          {emp.jobTitle ? emp.jobTitle : ""}
                          {emp.departmentName ? ` — ${emp.departmentName}` : ""}
                          {emp.branchName ? ` — ${emp.branchName}` : ""}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <div className="d-flex gap-2 mb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-link px-0"
                  onClick={() =>
                    setSelectedEmployeeIds((prev) => {
                      const next = new Set(prev);
                      mixedEmployees.matched.forEach((emp) => next.add(emp.id));
                      return [...next];
                    })
                  }
                >
                  إضافة كل المطابقين
                </button>
                {selectedEmployeeIds.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link px-0"
                    onClick={() => setSelectedEmployeeIds([])}
                  >
                    مسح التحديد
                  </button>
                )}
              </div>
              <div className="hr-evaluations__pick-list">
                {mixedEmployees.shown.length === 0 ? (
                  <p className="text-muted p-2 mb-0">
                    لا يوجد موظفون مطابقون للفلاتر
                  </p>
                ) : (
                  mixedEmployees.shown.map((emp) => {
                    const checked = selectedEmployeeIds.includes(emp.id);
                    return (
                      <label
                        key={emp.id}
                        className={`hr-evaluations__pick-item is-check ${
                          checked ? "is-selected" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setSelectedEmployeeIds((prev) =>
                              prev.includes(emp.id)
                                ? prev.filter((id) => id !== emp.id)
                                : [...prev, emp.id],
                            )
                          }
                        />
                        <span>
                          <strong>{emp.name || emp.fullName}</strong>
                          <span className="text-muted">
                            {" "}
                            {emp.code || emp.employeeCode}
                            {emp.jobTitle ? ` — ${emp.jobTitle}` : ""}
                            {emp.departmentName
                              ? ` — ${emp.departmentName}`
                              : ""}
                            {emp.branchName ? ` — ${emp.branchName}` : ""}
                          </span>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <div className="mb-3">
              {targetType === "branch" ? (
                <div className="mb-2">
                  <label className="form-label">
                    اختر الفروع (نفس الأسئلة لكل فرع في نفس الشهر)
                  </label>
                  <div className="hr-evaluations__multi-list">
                    {branchOptions.map((branch) => {
                      const id = optionId(branch);
                      const checked = selectedBranchIds.includes(id);
                      return (
                        <label
                          key={id}
                          className={`hr-evaluations__check-item ${
                            checked ? "is-checked" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              toggleSelectedId(
                                selectedBranchIds,
                                setSelectedBranchIds,
                                id,
                              )
                            }
                          />
                          <span>{branch.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  {branchOptions.length > 0 && (
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-link px-0"
                        onClick={() =>
                          setSelectedBranchIds(branchOptions.map(optionId))
                        }
                      >
                        تحديد الكل
                      </button>
                      {selectedBranchIds.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link px-0"
                          onClick={() => setSelectedBranchIds([])}
                        >
                          مسح التحديد
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-2">
                  <label className="form-label">
                    اختر الإدارات (نفس الأسئلة لكل إدارة)
                  </label>
                  <div className="hr-evaluations__multi-list">
                    {deptOptions.map((dept) => {
                      const id = optionId(dept);
                      const checked = selectedDepartmentIds.includes(id);
                      return (
                        <label
                          key={id}
                          className={`hr-evaluations__check-item ${
                            checked ? "is-checked" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              toggleSelectedId(
                                selectedDepartmentIds,
                                setSelectedDepartmentIds,
                                id,
                              )
                            }
                          />
                          <span>{dept.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  {deptOptions.length > 0 && (
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-link px-0"
                        onClick={() =>
                          setSelectedDepartmentIds(deptOptions.map(optionId))
                        }
                      >
                        تحديد الكل
                      </button>
                      {selectedDepartmentIds.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link px-0"
                          onClick={() => setSelectedDepartmentIds([])}
                        >
                          مسح التحديد
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="mb-2">
                <label className="form-label">الوظيفة (اختياري)</label>
                <select
                  className="form-control"
                  value={targetJobTitle}
                  aria-label="فلتر الوظيفة"
                  onChange={(e) => setTargetJobTitle(e.target.value)}
                >
                  <option value="">كل الوظائف</option>
                  {scopeJobOptions.map((jobTitle) => (
                    <option key={jobTitle} value={jobTitle}>
                      {jobTitle}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-muted hr-evaluations__filter-count">
                {targetType === "branch" && selectedBranchIds.length === 0
                  ? "اختر فرعاً واحداً أو أكثر — تُنشأ مسودة لكل فرع بنفس الأسئلة"
                  : targetType === "department" &&
                      selectedDepartmentIds.length === 0
                    ? "اختر إدارة واحدة أو أكثر — تُنشأ مسودة لكل إدارة بنفس الأسئلة"
                    : `سيتم تقييم ${scopedEmployees.length} موظف عبر ${
                        targetType === "branch"
                          ? selectedBranchIds.length
                          : selectedDepartmentIds.length
                      } نطاق — نفس الأسئلة والتواريخ`}
              </p>
              <div className="hr-evaluations__pick-list">
                {scopedEmployees.length === 0 ? (
                  <p className="text-muted p-2 mb-0">
                    لا يوجد موظفون مطابقون لهذا النطاق
                  </p>
                ) : (
                  scopedEmployees.slice(0, 80).map((emp) => (
                    <div
                      className="hr-evaluations__pick-item is-static"
                      key={emp.id}
                    >
                      <strong>{emp.name || emp.fullName}</strong>
                      <span className="text-muted">
                        {emp.code || emp.employeeCode}
                        {emp.jobTitle ? ` — ${emp.jobTitle}` : ""}
                        {emp.departmentName ? ` — ${emp.departmentName}` : ""}
                        {emp.branchName ? ` — ${emp.branchName}` : ""}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="hr-evaluations__questions">
            <div className="hr-evaluations__questions-head">
              <h3>أسئلة التقييم</h3>
              <span
                className={
                  allWeightsValid
                    ? "hr-evaluations__weight-ok"
                    : "hr-evaluations__weight-bad"
                }
              >
                مجموع الأوزان: {totalWeight}%
              </span>
            </div>
            <p className="text-muted small mb-2">
              التقييم الذاتي يشمل أسئلة الجمهور. يمكن إرسال سؤال لموظف بالاسم
              ليكون هو من يقيّمه. وبدون تحديد موظفين خاضعين، السؤال لكل المجموعة.
            </p>
            {questions.map((q, index) => (
              <div className="hr-evaluations__question-block" key={index}>
                <div className="hr-evaluations__question-row">
                  <input
                    className="form-control"
                    placeholder="نص السؤال"
                    value={q.text}
                    onChange={(e) =>
                      updateQuestion(index, "text", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    className="form-control"
                    placeholder="الوزن %"
                    min="0"
                    max="100"
                    step="0.01"
                    value={q.weightPercent}
                    onChange={(e) =>
                      updateQuestion(index, "weightPercent", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    className="form-control"
                    placeholder="أقصى درجة"
                    min="1"
                    max="100"
                    value={q.maxScore}
                    onChange={(e) =>
                      updateQuestion(index, "maxScore", e.target.value)
                    }
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeQuestion(index)}
                  >
                    حذف
                  </button>
                </div>
                <div className="hr-evaluations__audience-row">
                  <span className="hr-evaluations__audience-label">
                    يُقيَّم أيضاً بواسطة:
                  </span>
                  {AUDIENCE_OPTIONS.map((opt) => {
                    const checked = (q.audiences || []).includes(opt.key);
                    return (
                      <label
                        key={opt.key}
                        className={`hr-evaluations__audience-chip ${
                          checked ? "is-checked" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAudience(index, opt.key)}
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
                <QuestionSubjectPicker
                  employees={questionCandidateEmployees}
                  selectedIds={q.targetEmployeeIds}
                  labelPrefix={`السؤال ${index + 1} لـ`}
                  onChange={(next) =>
                    updateQuestion(index, "targetEmployeeIds", next)
                  }
                />
                <QuestionRaterPicker
                  employees={allEmployees}
                  selectedIds={q.raterEmployeeIds}
                  labelPrefix={`يقيّم السؤال ${index + 1}:`}
                  onChange={(next) =>
                    updateQuestion(index, "raterEmployeeIds", next)
                  }
                />
              </div>
            ))}
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={addQuestion}
            >
              إضافة سؤال
            </button>
          </div>

          <div className="hr-evaluations__form-actions">
            <button
              type="submit"
              className="btn btn-success"
              disabled={submitting}
            >
              {submitting ? "جاري الحفظ..." : "حفظ كمسودة"}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetForm}
            >
              إلغاء
            </button>
          </div>
        </form>
      )}

      <div className="hr-evaluations__layout">
        <div className="hr-evaluations__panel">
          <h2 className="hr-evaluations__panel-title">الجولات</h2>
          {loading ? (
            <p className="text-muted">جاري التحميل...</p>
          ) : rounds.length === 0 ? (
            <p className="text-muted">لا توجد جولات بعد</p>
          ) : (
            <div className="hr-evaluations__round-list">
              {rounds.map((round) => {
                const subjects = round.subjects || [];
                const totalRaters = subjects.reduce(
                  (s, sub) => s + (sub.raters?.length || 0),
                  0,
                );
                const submitted = subjects.reduce(
                  (s, sub) =>
                    s +
                    (sub.raters || []).filter((r) => r.status === "SUBMITTED")
                      .length,
                  0,
                );
                return (
                  <button
                    type="button"
                    key={round.id}
                    className={`hr-evaluations__round-card ${
                      selectedRoundId === round.id ? "is-selected" : ""
                    }`}
                    onClick={() => loadRoundDetail(round.id)}
                  >
                    <div className="hr-evaluations__round-card-top">
                      <strong>{round.title}</strong>
                      <span
                        className={`hr-evaluations__badge status-${(
                          round.status || ""
                        ).toLowerCase()}`}
                      >
                        {statusLabel(round.status)}
                      </span>
                    </div>
                    <div className="text-muted small">{roundTargetLabel(round)}</div>
                    <div className="text-muted small">
                      تقدم المقيمين: {submitted}/{totalRaters}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="hr-evaluations__panel">
          <h2 className="hr-evaluations__panel-title">التفاصيل والنتائج</h2>
          {detailLoading ? (
            <p className="text-muted">جاري التحميل...</p>
          ) : !selectedRound ? (
            <p className="text-muted">اختر جولة لعرض التفاصيل</p>
          ) : (
            <>
              <div className="hr-evaluations__detail-actions">
                {selectedRound.status === "DRAFT" && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={submitting}
                      onClick={() =>
                        editingDraftQuestions
                          ? setEditingDraftQuestions(false)
                          : startEditDraftQuestions()
                      }
                    >
                      {editingDraftQuestions
                        ? "إلغاء تعديل الأسئلة"
                        : "تعديل الأسئلة والتواريخ"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={submitting}
                      onClick={() => handleActivate(selectedRound.id)}
                    >
                      تفعيل
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      disabled={submitting}
                      onClick={() =>
                        handleDelete(selectedRound.id, selectedRound.status)
                      }
                    >
                      حذف
                    </button>
                  </>
                )}
                {selectedRound.status === "CLOSED" && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    disabled={submitting}
                    onClick={() =>
                      handleDelete(selectedRound.id, selectedRound.status)
                    }
                  >
                    حذف التقييم المغلق
                  </button>
                )}
                {selectedRound.status === "ACTIVE" && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    disabled={submitting}
                    onClick={() => handleClose(selectedRound.id)}
                  >
                    إغلاق وحساب النتائج
                  </button>
                )}
                {(selectedRound.status === "ACTIVE" ||
                  selectedRound.status === "CLOSED") && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={submitting}
                    onClick={() =>
                      showRepublish
                        ? setShowRepublish(false)
                        : openRepublishPanel()
                    }
                  >
                    {showRepublish
                      ? "إلغاء إعادة النشر"
                      : "إعادة نشر لشهر آخر"}
                  </button>
                )}
                {(selectedRound.questions || []).length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    disabled={submitting}
                    onClick={() =>
                      showSpread ? setShowSpread(false) : openSpreadPanel()
                    }
                  >
                    {showSpread
                      ? "إلغاء الإرسال"
                      : spreadTargetMode === "department"
                        ? "إرسال لإدارات أخرى (نفس الشهر)"
                        : "إرسال لفروع أخرى (نفس الشهر)"}
                  </button>
                )}
                {selectedRound.status !== "CLOSED" && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={submitting}
                    onClick={() =>
                      showJobRaterPanel
                        ? setShowJobRaterPanel(false)
                        : openJobRaterPanel()
                    }
                  >
                    {showJobRaterPanel
                      ? "إلغاء إضافة المسمى"
                      : "إضافة مقيمين بمسمى وظيفي"}
                  </button>
                )}
              </div>

              {showRepublish &&
                (selectedRound.status === "ACTIVE" ||
                  selectedRound.status === "CLOSED") && (
                  <div className="mb-3 hr-evaluations__draft-edit">
                    <h4>إعادة نشر نفس التقييم</h4>
                    <p className="text-muted small">
                      تُنشأ مسودة جديدة بنفس الأسئلة والنطاق والموظفين (حسب
                      الوضع الحالي). يمكنك تعديل الأسئلة والتواريخ قبل التفعيل.
                      الجولة الحالية ونتائجها تبقى كما هي.
                    </p>
                    <div className="mb-2">
                      <label className="form-label">عنوان الجولة الجديدة</label>
                      <input
                        className="form-control"
                        value={republishTitle}
                        onChange={(e) => setRepublishTitle(e.target.value)}
                      />
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-md-6">
                        <label className="form-label">من تاريخ</label>
                        <input
                          type="date"
                          className="form-control"
                          value={republishStart}
                          onChange={(e) => setRepublishStart(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">إلى تاريخ</label>
                        <input
                          type="date"
                          className="form-control"
                          value={republishEnd}
                          onChange={(e) => setRepublishEnd(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="hr-evaluations__month-presets mb-2">
                      <span className="form-text mb-0">شهر سريع:</span>
                      {[0, 1, 2].map((offset) => {
                        const label =
                          offset === 0
                            ? "هذا الشهر"
                            : offset === 1
                              ? "الشهر القادم"
                              : "بعد شهرين";
                        return (
                          <button
                            key={offset}
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => applyRepublishMonthPreset(offset)}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="hr-evaluations__form-actions">
                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={submitting}
                        onClick={handleRepublish}
                      >
                        {submitting
                          ? "جاري الإنشاء..."
                          : "إنشاء مسودة للشهر المحدد"}
                      </button>
                    </div>
                  </div>
                )}

              {showSpread && (selectedRound.questions || []).length > 0 && (
                <div className="mb-3 hr-evaluations__draft-edit">
                  <h4>
                    {spreadTargetMode === "department"
                      ? "إرسال نفس الأسئلة لإدارات أخرى"
                      : "إرسال نفس الأسئلة لفروع أخرى"}
                  </h4>
                  <p className="text-muted small">
                    تُنسخ الأسئلة وتواريخ نفس الشهر من هذه الجولة. اختر فرعاً
                    أو أكثر، أو كل الفروع مرة واحدة. الجولة الحالية تبقى كما هي.
                  </p>
                  {spreadTargetMode === "department" ? (
                    <>
                      <div className="hr-evaluations__multi-list mb-2">
                        {spreadDepartmentOptions.map((dept) => {
                          const id = optionId(dept);
                          const checked = spreadDepartmentIds.includes(id);
                          return (
                            <label
                              key={id}
                              className={`hr-evaluations__check-item ${
                                checked ? "is-checked" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  toggleSelectedId(
                                    spreadDepartmentIds,
                                    setSpreadDepartmentIds,
                                    id,
                                  )
                                }
                              />
                              <span>{dept.name}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="d-flex gap-2 mb-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-link px-0"
                          onClick={() =>
                            setSpreadDepartmentIds(
                              spreadDepartmentOptions.map(optionId),
                            )
                          }
                        >
                          تحديد كل الإدارات
                        </button>
                        {spreadDepartmentIds.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link px-0"
                            onClick={() => setSpreadDepartmentIds([])}
                          >
                            مسح التحديد
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hr-evaluations__multi-list mb-2">
                        {spreadBranchOptions.map((branch) => {
                          const id = optionId(branch);
                          const checked = spreadBranchIds.includes(id);
                          return (
                            <label
                              key={id}
                              className={`hr-evaluations__check-item ${
                                checked ? "is-checked" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  toggleSelectedId(
                                    spreadBranchIds,
                                    setSpreadBranchIds,
                                    id,
                                  )
                                }
                              />
                              <span>{branch.name}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="d-flex gap-2 mb-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-link px-0"
                          onClick={() =>
                            setSpreadBranchIds(spreadBranchOptions.map(optionId))
                          }
                        >
                          تحديد كل الفروع
                        </button>
                        {spreadBranchIds.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link px-0"
                            onClick={() => setSpreadBranchIds([])}
                          >
                            مسح التحديد
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  <label className="hr-evaluations__check-item mb-2">
                    <input
                      type="checkbox"
                      checked={spreadActivate}
                      onChange={(e) => setSpreadActivate(e.target.checked)}
                    />
                    <span>تفعيل تلقائي بعد الإرسال (بدل المسودة)</span>
                  </label>
                  <div className="hr-evaluations__form-actions">
                    <button
                      type="button"
                      className="btn btn-success"
                      disabled={submitting}
                      onClick={handleSpread}
                    >
                      {submitting
                        ? "جاري الإرسال..."
                        : spreadActivate
                          ? "إرسال وتفعيل للنطاقات المحددة"
                          : "إنشاء مسودات للنطاقات المحددة"}
                    </button>
                  </div>
                </div>
              )}

              {showJobRaterPanel && selectedRound.status !== "CLOSED" && (
                <div className="mb-3 hr-evaluations__draft-edit">
                  <h4>إضافة مقيمين بمسمى وظيفي</h4>
                  <p className="text-muted small">
                    اختر مسمى (مثل مدير فرع) ليُضاف كل من يحملونه كمقيّمين على
                    الموظفين الخاضعين للتقييم. يمكن تطبيقها على كل جولات الفروع
                    في نفس الفترة دفعة واحدة.
                  </p>
                  <div className="row g-2 mb-2">
                    <div className="col-md-7">
                      <label className="form-label">المسمى الوظيفي</label>
                      <select
                        className="form-control"
                        value={jobRaterTitle}
                        onChange={(e) => setJobRaterTitle(e.target.value)}
                      >
                        <option value="">— اختر المسمى —</option>
                        {jobTitleOptions.map((title) => (
                          <option key={title} value={title}>
                            {title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">نوع المقيم</label>
                      <select
                        className="form-control"
                        value={jobRaterType}
                        onChange={(e) => setJobRaterType(e.target.value)}
                      >
                        <option value="CUSTOM">مخصص</option>
                        <option value="MANAGER">مدير</option>
                        <option value="PEER">زميل</option>
                        <option value="SUBORDINATE">مرؤوس</option>
                      </select>
                    </div>
                  </div>
                  <label className="hr-evaluations__check-item mb-1">
                    <input
                      type="checkbox"
                      checked={jobRaterSameBranch}
                      onChange={(e) => setJobRaterSameBranch(e.target.checked)}
                    />
                    <span>
                      من نفس فرع الموظف فقط. لمدير المنطقة: فروع منطقته
                    </span>
                  </label>
                  <label className="hr-evaluations__check-item mb-2">
                    <input
                      type="checkbox"
                      checked={jobRaterRelated}
                      onChange={(e) => setJobRaterRelated(e.target.checked)}
                    />
                    <span>
                      تطبيق على كل جولات الفروع/الإدارات في نفس الفترة
                    </span>
                  </label>
                  <div className="hr-evaluations__form-actions">
                    <button
                      type="button"
                      className="btn btn-success"
                      disabled={submitting || !jobRaterTitle}
                      onClick={handleAddRatersByJobTitle}
                    >
                      {submitting
                        ? "جاري الإضافة..."
                        : "إضافة المسمى كمقيّمين"}
                    </button>
                  </div>
                </div>
              )}

              {selectedRound.status === "DRAFT" && editingDraftQuestions ? (
                <div className="mb-3 hr-evaluations__draft-edit">
                  <h4>تعديل المسودة</h4>
                  <div className="mb-2">
                    <label className="form-label">العنوان</label>
                    <input
                      className="form-control"
                      value={draftEditTitle}
                      onChange={(e) => setDraftEditTitle(e.target.value)}
                    />
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-md-6">
                      <label className="form-label">من تاريخ</label>
                      <input
                        type="date"
                        className="form-control"
                        value={draftEditStart}
                        onChange={(e) => setDraftEditStart(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">إلى تاريخ</label>
                      <input
                        type="date"
                        className="form-control"
                        value={draftEditEnd}
                        onChange={(e) => setDraftEditEnd(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="hr-evaluations__month-presets mb-2">
                    <span className="form-text mb-0">شهر سريع:</span>
                    {[0, 1, 2].map((offset) => {
                      const preset = getMonthDefaultsOffset(offset);
                      const label =
                        offset === 0
                          ? "هذا الشهر"
                          : offset === 1
                            ? "الشهر القادم"
                            : "بعد شهرين";
                      return (
                        <button
                          key={offset}
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setDraftEditTitle(preset.title);
                            setDraftEditStart(preset.startDate);
                            setDraftEditEnd(preset.endDate);
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="hr-evaluations__questions-head">
                    <h5 className="mb-0">الأسئلة</h5>
                    <span
                      className={
                        draftEditWeightsValid
                          ? "hr-evaluations__weight-ok"
                          : "hr-evaluations__weight-bad"
                      }
                    >
                      مجموع الأوزان: {draftEditTotalWeight}%
                    </span>
                  </div>
                  {draftEditQuestions.map((q, index) => (
                    <div className="hr-evaluations__question-block" key={index}>
                      <div className="hr-evaluations__question-row">
                        <input
                          className="form-control"
                          placeholder="نص السؤال"
                          value={q.text}
                          onChange={(e) =>
                            updateDraftEditQuestion(
                              index,
                              "text",
                              e.target.value,
                            )
                          }
                        />
                        <input
                          type="number"
                          className="form-control"
                          placeholder="الوزن %"
                          min="0"
                          max="100"
                          step="0.01"
                          value={q.weightPercent}
                          onChange={(e) =>
                            updateDraftEditQuestion(
                              index,
                              "weightPercent",
                              e.target.value,
                            )
                          }
                        />
                        <input
                          type="number"
                          className="form-control"
                          placeholder="أقصى درجة"
                          min="1"
                          max="100"
                          value={q.maxScore}
                          onChange={(e) =>
                            updateDraftEditQuestion(
                              index,
                              "maxScore",
                              e.target.value,
                            )
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeDraftEditQuestion(index)}
                        >
                          حذف
                        </button>
                      </div>
                      <div className="hr-evaluations__audience-row">
                        <span className="hr-evaluations__audience-label">
                          يُقيَّم أيضاً بواسطة:
                        </span>
                        {AUDIENCE_OPTIONS.map((opt) => {
                          const checked = (q.audiences || []).includes(opt.key);
                          return (
                            <label
                              key={opt.key}
                              className={`hr-evaluations__audience-chip ${
                                checked ? "is-checked" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  toggleDraftEditAudience(index, opt.key)
                                }
                              />
                              {opt.label}
                            </label>
                          );
                        })}
                      </div>
                      <QuestionSubjectPicker
                        employees={subjectPickerEmployees(
                          selectedRound,
                          employeesById,
                        )}
                        selectedIds={q.targetEmployeeIds}
                        labelPrefix={`تعديل السؤال ${index + 1} لـ`}
                        onChange={(next) =>
                          updateDraftEditQuestion(
                            index,
                            "targetEmployeeIds",
                            next,
                          )
                        }
                      />
                      <QuestionRaterPicker
                        employees={allEmployees}
                        selectedIds={q.raterEmployeeIds}
                        labelPrefix={`يقيّم تعديل السؤال ${index + 1}:`}
                        onChange={(next) =>
                          updateDraftEditQuestion(
                            index,
                            "raterEmployeeIds",
                            next,
                          )
                        }
                      />
                    </div>
                  ))}
                  <div className="hr-evaluations__form-actions">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={addDraftEditQuestion}
                    >
                      إضافة سؤال
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      disabled={submitting}
                      onClick={handleSaveDraftQuestions}
                    >
                      {submitting ? "جاري الحفظ..." : "حفظ تعديلات المسودة"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-3">
                  <h4>الأسئلة</h4>
                  {(selectedRound.startDate || selectedRound.endDate) && (
                    <p className="text-muted small">
                      الفترة:{" "}
                      {selectedRound.startDate
                        ? String(selectedRound.startDate).slice(0, 10)
                        : "—"}{" "}
                      →{" "}
                      {selectedRound.endDate
                        ? String(selectedRound.endDate).slice(0, 10)
                        : "—"}
                    </p>
                  )}
                  <ul className="hr-evaluations__q-list">
                    {(selectedRound.questions || []).map((q) => (
                      <li key={q.id}>
                        {q.text}{" "}
                        <span className="text-muted">
                          ({Number(q.weightPercent)}% — من {q.maxScore} —{" "}
                          {audienceLabels(q.audiences)} —{" "}
                          {questionTargetLabel(q, selectedRound.subjects)} —{" "}
                          {questionRaterLabel(q, employeesById)})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {roundSubjects.length > 0 && (
                <div className="mb-3">
                  <h4>الموظفون الخاضعون للتقييم</h4>
                  <OrgFilters
                    branchValue={subjectBranchFilter}
                    departmentValue={subjectDepartmentFilter}
                    jobValue={subjectJobFilter}
                    branches={subjectBranchOptions}
                    departments={subjectDepartmentOptions}
                    jobs={subjectJobOptions}
                    onBranch={(value) => {
                      const nextDepartments = (
                        value
                          ? roundSubjects.filter((row) => row.branchKey === value)
                          : roundSubjects
                      ).map((row) => row.departmentKey);
                      const nextDepartment = nextDepartments.includes(
                        subjectDepartmentFilter,
                      )
                        ? subjectDepartmentFilter
                        : "";
                      const nextJobs = roundSubjects
                        .filter((row) => {
                          if (value && row.branchKey !== value) return false;
                          if (nextDepartment && row.departmentKey !== nextDepartment) {
                            return false;
                          }
                          return true;
                        })
                        .map((row) => row.jobKey);
                      setSubjectBranchFilter(value);
                      setSubjectDepartmentFilter(nextDepartment);
                      setSubjectJobFilter(
                        nextJobs.includes(subjectJobFilter) ? subjectJobFilter : "",
                      );
                    }}
                    onDepartment={(value) => {
                      const nextJobs = roundSubjects
                        .filter((row) => {
                          if (
                            subjectBranchFilter &&
                            row.branchKey !== subjectBranchFilter
                          ) {
                            return false;
                          }
                          if (value && row.departmentKey !== value) return false;
                          return true;
                        })
                        .map((row) => row.jobKey);
                      setSubjectDepartmentFilter(value);
                      setSubjectJobFilter(
                        nextJobs.includes(subjectJobFilter) ? subjectJobFilter : "",
                      );
                    }}
                    onJob={setSubjectJobFilter}
                  />
                  <p className="text-muted hr-evaluations__filter-count">
                    ظاهر {visibleSubjects.length} من {roundSubjects.length} موظف
                  </p>
                  {visibleSubjects.length === 0 && (
                    <p className="text-muted">لا يوجد موظفون مطابقون للفلاتر</p>
                  )}
                </div>
              )}

              {visibleSubjects.map(({ subject, branchName, departmentKey }) => {
                const submittedCount = (subject.raters || []).filter(
                  (r) => r.status === "SUBMITTED",
                ).length;
                const total = subject.raters?.length || 0;
                return (
                  <div className="hr-evaluations__subject" key={subject.id}>
                    <div className="hr-evaluations__subject-head">
                      <div>
                        <strong>
                          {subject.employee?.fullName || "موظف"}
                        </strong>
                        <div className="text-muted small">
                          {subject.employee?.employeeCode}
                          {subject.employee?.jobTitle
                            ? ` — ${subject.employee.jobTitle}`
                            : ""}
                          {departmentKey ? ` — ${departmentKey}` : ""}
                          {branchName ? ` — ${branchName}` : ""} — اكتمال{" "}
                          {submittedCount}/{total}
                          {subject.finalPercent != null && (
                            <>
                              {" "}
                              — النتيجة:{" "}
                              <span className="hr-evaluations__percent">
                                {Number(subject.finalPercent)}%
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {selectedRound.status !== "CLOSED" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => startEditRaters(subject)}
                        >
                          تعديل المقيمين
                        </button>
                      )}
                    </div>

                    {editingSubjectId === subject.id ? (
                      <div className="hr-evaluations__rater-edit">
                        <div className="row g-2 mb-2">
                          <div className="col-md-5">
                            <select
                              className="form-control"
                              value={draftRaterType}
                              onChange={(e) =>
                                setDraftRaterType(e.target.value)
                              }
                            >
                              <option value="SELF">تقييم ذاتي</option>
                              {AUDIENCE_OPTIONS.map((g) => (
                                <option key={g.key} value={g.key}>
                                  {g.label}
                                </option>
                              ))}
                              <option value="CUSTOM">مخصص</option>
                            </select>
                          </div>
                          <div className="col-md-7">
                            <input
                              className="form-control"
                              placeholder="بحث لإضافة مقيم..."
                              value={raterSearch}
                              onChange={(e) => setRaterSearch(e.target.value)}
                            />
                          </div>
                        </div>
                        <OrgFilters
                          branchValue={raterBranchFilter}
                          jobValue={raterJobFilter}
                          branches={raterBranchOptions}
                          jobs={raterJobOptions}
                          onBranch={(value) => {
                            setRaterBranchFilter(value);
                            keepJobIfValid(raterPool, value, setRaterJobFilter);
                          }}
                          onJob={setRaterJobFilter}
                        />
                        <p className="text-muted hr-evaluations__filter-count">
                          ظاهر {raterPickList.shown.length} من{" "}
                          {raterPickList.total} موظف
                        </p>
                        <ul className="hr-evaluations__rater-list mb-2">
                          {raterDraft.map((r) => (
                            <li key={`${r.employeeId}:${r.raterType}`}>
                              <span>
                                {r.name || r.employeeId} (
                                {raterTypeLabel(r.raterType)})
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>
                                  removeRaterDraft(r.employeeId, r.raterType)
                                }
                              >
                                حذف
                              </button>
                            </li>
                          ))}
                        </ul>
                        <div className="hr-evaluations__pick-list">
                          {raterPickList.shown.length === 0 ? (
                            <p className="text-muted p-2 mb-0">
                              لا يوجد موظفون مطابقون للفلاتر
                            </p>
                          ) : (
                            raterPickList.shown.map((emp) => (
                              <button
                                type="button"
                                key={emp.id}
                                className="hr-evaluations__pick-item"
                                onClick={() =>
                                  addRaterDraft(emp, draftRaterType)
                                }
                              >
                                <strong>{emp.name || emp.fullName}</strong>
                                <span className="text-muted">
                                  {emp.code || emp.employeeCode}
                                  {emp.jobTitle ? ` — ${emp.jobTitle}` : ""}
                                  {emp.branchName ? ` — ${emp.branchName}` : ""}
                                  {" — إضافة كـ "}
                                  {raterTypeLabel(draftRaterType)}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                        <div className="hr-evaluations__form-actions">
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            disabled={submitting}
                            onClick={() => saveRaters(subject.id)}
                          >
                            حفظ المقيمين
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setEditingSubjectId(null)}
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <ul className="hr-evaluations__rater-list">
                          {(subject.raters || []).map((r) => {
                            const notes = raterNotes(
                              r,
                              selectedRound.questions,
                            );
                            return (
                              <li
                                key={r.id}
                                className="hr-evaluations__rater-item"
                              >
                                <div className="hr-evaluations__rater-row">
                                  <span>
                                    {r.rater?.fullName || "—"} (
                                    {raterTypeLabel(r.raterType)})
                                  </span>
                                  <span>
                                    {r.status === "SUBMITTED"
                                      ? `${Number(r.scorePercent)}%`
                                      : "لم يُرسل"}
                                  </span>
                                </div>
                                {r.status === "SUBMITTED" && notes.hasAny && (
                                  <div className="hr-evaluations__notes">
                                    {notes.general ? (
                                      <p>
                                        <strong>ملاحظة عامة: </strong>
                                        {notes.general}
                                      </p>
                                    ) : null}
                                    {notes.answerNotes.map((n, idx) => (
                                      <p key={`${r.id}-note-${idx}`}>
                                        <strong>{n.question}: </strong>
                                        {n.comment}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                        <EvaluationComparisonChart
                          perspective="hr"
                          questions={selectedRound.questions}
                          raters={subject.raters}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
